import { useState, useEffect } from "react";
import SectionWrapper from "@/components/base/SectionWrapper";
import { supabase } from "@/lib/supabase";

interface SiteSettings {
  address: string;
  email: string;
  phone: string;
  facebook: string;
  tiktok: string;
  instagram: string;
  linkedin: string;
}

export default function ContactPage() {
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    address: "", email: "", phone: "", facebook: "", tiktok: "", instagram: "", linkedin: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success">("idle");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const { data } = await supabase.from("site_settings").select("key, value");
      const map: Record<string, string> = {};
      (data || []).forEach((s) => { map[s.key] = s.value; });
      setSiteSettings({
        address: map.address || "", email: map.email || "", phone: map.phone || "",
        facebook: map.facebook || "", tiktok: map.twitter || "", instagram: map.instagram || "", linkedin: map.linkedin || "",
      });
      setIsLoading(false);
    };
    fetchData();
  }, []);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="w-10 h-10 border-2 border-primary-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden bg-primary-900">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative z-10 text-center px-4">
          <p className="text-gold-400 text-xs font-medium uppercase tracking-[2px] mb-3">Reach Out</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white">Contact Us</h1>
        </div>
      </section>

      <SectionWrapper className="py-12 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-10">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-primary-900 mb-2">Send a Message</h3>
              <p className="text-gray-500 text-sm mb-6">We would love to hear from you. Fill out the form below and we will get back to you as soon as possible.</p>
              {formStatus === "success" && (
                <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm">
                  <i className="ri-check-line mr-1" /> Message sent successfully!
                </div>
              )}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setFormStatus("submitting");
                  const fd = new FormData(e.currentTarget);
                  const { error } = await supabase.from("contact_messages").insert({
                    name: fd.get("name") as string,
                    email: fd.get("email") as string,
                    subject: fd.get("subject") as string,
                    message: fd.get("message") as string,
                  });
                  if (!error) {
                    setFormStatus("success");
                    (e.target as HTMLFormElement).reset();
                    setTimeout(() => setFormStatus("idle"), 3000);
                  } else {
                    setFormStatus("idle");
                  }
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="text" name="name" placeholder="Full Name" required className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" />
                  <input type="email" name="email" placeholder="Email Address" required className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" />
                </div>
                <input type="text" name="subject" placeholder="Subject" required className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" />
                <textarea name="message" placeholder="Your Message" required maxLength={500} rows={6} className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors resize-none" />
                <button type="submit" disabled={formStatus === "submitting"} className="w-full py-3.5 bg-gold-600 hover:bg-gold-700 text-white font-medium rounded-lg transition-colors text-sm disabled:opacity-60">
                  {formStatus === "submitting" ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-primary-900 rounded-2xl p-6 md:p-8 text-white">
                <h3 className="font-heading font-bold text-xl md:text-2xl mb-6">Contact Information</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0"><i className="ri-map-pin-line text-gold-400" /></div>
                    <div><p className="font-medium text-sm mb-1">Address</p><p className="text-white/70 text-sm leading-relaxed">{siteSettings.address}</p></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0"><i className="ri-mail-line text-gold-400" /></div>
                    <div><p className="font-medium text-sm mb-1">Email</p><a href={`mailto:${siteSettings.email}`} className="text-white/70 text-sm hover:text-gold-400 transition-colors">{siteSettings.email}</a></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0"><i className="ri-phone-line text-gold-400" /></div>
                    <div><p className="font-medium text-sm mb-1">Phone</p><a href={`tel:${siteSettings.phone}`} className="text-white/70 text-sm hover:text-gold-400 transition-colors">{siteSettings.phone}</a></div>
                  </div>
                </div>
                <div className="mt-8">
                  <p className="font-medium text-sm mb-3">Follow Us</p>
                  <div className="flex items-center gap-3">
                    {siteSettings.facebook && <a href={siteSettings.facebook} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="Facebook"><i className="ri-facebook-fill" /></a>}
                    {siteSettings.tiktok && <a href={siteSettings.tiktok} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="Twitter"><i className="ri-tiktok-x-fill" /></a>}
                    {siteSettings.instagram && <a href={siteSettings.instagram} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="Instagram"><i className="ri-instagram-line" /></a>}
                    {siteSettings.linkedin && <a href={siteSettings.linkedin} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="LinkedIn"><i className="ri-linkedin-fill" /></a>}
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <iframe src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d15903.437158247758!2d6.9810679!3d4.7941918!3m2!1i1024!2i768!4f13.1!2m1!1srivers%20state%20university%20on%20google%20maps!5e0!3m2!1sen!2sng!4v1779168948021!5m2!1sen!2sng" width="100%" height="320" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Rivers State University Location" className="rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
}